import { useEffect, useState } from 'react';
import { Loader, Table, Button, useMantineTheme } from '@mantine/core';
import { useDropzone } from 'react-dropzone';
import { IconUpload } from '@tabler/icons-react';
import { useDocumentStyles } from './style';
import { baseURL } from '../../../config/urls';
import {
  getHeader,
  getUserData,
  getUserType
} from '../../../config/userSession';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from 'usehooks-ts';
import { listDocuments } from '../../../graphql/queries';
import { Amplify } from 'aws-amplify';
import { generateClient } from 'aws-amplify/api';
import { useChatState } from '../Assistant/context';
import { useGetUser } from '../../../hooks/use-get-user';
const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [uploading, setUploading] = useState(false);
  const theme = useMantineTheme(); // Get the theme object
  const { email } = getUserData();
  const { userId } = useChatState();
  function capitalizeFirstLetter(string) {
    return string?.charAt(0)?.toUpperCase() + string?.slice(1);
  }
  const userType = getUserType();
  const userData = useGetUser();
  const IDPACCESS = userData?.user?.UserAttributes?.find(
    (attr) => attr.Name === 'custom:idp_access'
  )?.Value;
  const navigate = useNavigate();

  useEffect(() => {
    if (IDPACCESS !== 'APPROVED' && userData?.user?.UserAttributes?.length > 0) {
      navigate(`/${userType.toLowerCase()}/`);
    }
  }, [userData]);
  const capitalizedUserId = capitalizeFirstLetter(email);
  const { classes } = useDocumentStyles();
  const [loading, setLoading] = useState(true);
  const [fetchMore] = useState(() => () => {}); // Placeholder for fetchMore function
  const [selectedFile, setSelectedFile] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const client = generateClient();

  Amplify.configure({
    API: {
      GraphQL: {
        endpoint: process.env.REACT_APP_API_URI,
        region: process.env.REACT_APP_AWS_REGION,
        defaultAuthMode: 'apiKey',
        apiKey: process.env.REACT_APP_APPSYNC_API_KEY
      }
    }
  });
  console.log(userId);
  const getAllListDocuments = async () => {
    const data = await client.graphql({
      query: listDocuments,
      variables: {
        filter: {
          uploadedBy: { eq: capitalizedUserId } // Replace "user123" with the actual user ID
        }
      }
    });
    console.log(data);
    if (data?.data?.listDocuments?.items) {
      setLoading(false);
    }
    setDocuments(data?.data?.listDocuments?.items);
    setNextToken(data?.data?.listDocuments?.nextToken);

    console.log(data?.listDocuments?.items);
  };

  useEffect(() => {
    getAllListDocuments();
  }, []);
  console.log('Documents:', documents);

  const handleRefresh = async () => {
    getAllListDocuments();
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'application/pdf': [] },
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];

      if (!file || file.type !== 'application/pdf') {
        notifications.show({
          message: 'Only PDF files are allowed',
          color: 'red'
        });
        return;
      }

      setSelectedFile(file);
    }
  });

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);

    const formData = new FormData();
    formData.append('file', selectedFile);

    formData.append('username', capitalizedUserId);

    const authorization = getHeader().Authorization;
    console.log(authorization);

    try {
      const response = await fetch(`${baseURL}/document/upload-document`, {
        method: 'POST',
        body: formData,
        headers: {
          authorization
        }
      });
      const data = await response.json();
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error);
      }

      setSelectedFile(null);

      notifications.show({
        message: 'File Uploaded'
      });
    } catch (error) {
      notifications.show({
        message: error.message || 'Something went wrong'
      });
    } finally {
      setUploading(false);
    }
  };

  const { isDarkMode } = useDarkMode();
  const sortedDocuments = [...(documents || [])].sort(
    (a, b) =>
      new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
  );

  console.log('Sorted Documents (Newest First):', sortedDocuments);
  return (
    <div
      className={classes.container}
      style={{
        backgroundColor: !isDarkMode ? '#FFFFFF' : 'white',
        borderLeft: '1px solid #ccc' // Add this line
      }}
    >
      <section
        className={classes.uploadSection}
        style={{
          background: `linear-gradient(121.51deg, #E1F4F5 30.38%, #E1F4F5 53.04%, #D6EBF4 71.29%)`,
          color: isDarkMode ? '#FFFFFF' : '#000000'
        }}
      >
        <div className={classes.uploadHeader}>
          <div {...getRootProps()} className={classes.dropzone}>
            <input {...getInputProps()} />
            {selectedFile ? (
              <div
                className={classes.filePreview}
                style={{
                  color: isDarkMode ? '#FFFFFF' : '#000000'
                }}
              >
                <p>
                  Selected File: <strong>{selectedFile.name}</strong>
                </p>
              </div>
            ) : (
              <div
                className={classes.dropzoneContent}
                style={{
                  color: '#000000'
                }}
              >
                <IconUpload size={48} stroke={1.5} />
                <p>Drag & drop PDFs here, or click to select files</p>
              </div>
            )}
          </div>
        </div>
        <button
          onClick={handleUpload}
          className={classes.uploadButton}
          disabled={!selectedFile || uploading}
          style={{
            background: `${theme.colors.primary[0]}`,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '8px 12px',
            borderRadius: '6px',
            color: '#fff',
            border: 'none',
            opacity: !selectedFile || uploading ? 0.6 : 1, // Reduce opacity when disabled
            cursor: !selectedFile || uploading ? 'not-allowed' : 'pointer' // Change cursor
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            height={24}
            width={24}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z"
            />
          </svg>
          {uploading ? 'Uploading...' : 'Upload Document'}
        </button>
      </section>

      {/* Table Section */}
      <section
        className={classes.tableSection}
        style={{
          background: `linear-gradient(121.51deg, #E1F4F5 30.38%, #E1F4F5 53.04%, #D6EBF4 71.29%)`,
          color: '#000000'
        }}
      >
        <div className={classes.tableHeader}>
          <h2 className={classes.tableTitle}>All Documents</h2>
          <button
            onClick={handleRefresh}
            className={`${classes.refreshButton} ${
              isRefreshing ? classes.refreshButtonLoading : ''
            }`}
            style={{
              background: `${theme.colors.primary[0]}`
            }}
            disabled={isRefreshing}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <div
          className={classes.tableResponsiveWrapper}
          style={{ maxHeight: '400px', overflowY: 'auto' }}
        >
          <Table
            className={classes.table}
            style={{
              backgroundColor: !isDarkMode ? '#3B4C69' : '#DAF4F4',
              color: '#FFFFFF',
              borderCollapse: 'collapse',
              width: '100%'
            }}
          >
            <thead
              style={{
                position: 'sticky',
                top: 0,
                backgroundColor: `${theme.colors.primary[0]}`,
                zIndex: 1
              }}
            >
              <tr>
                <th
                  style={{
                    color: '#FFFFFF',
                    width: '25%'
                  }}
                >
                  Id
                </th>
                <th
                  style={{
                    color: '#FFFFFF',
                    width: '25%'
                  }}
                >
                  Title
                </th>
                <th
                  style={{
                    color: '#FFFFFF',
                    width: '25%'
                  }}
                >
                  Last Updated
                </th>
                <th
                  style={{
                    color: '#FFFFFF',
                    width: '25%'
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedDocuments.map((doc) => (
                <tr
                  key={doc.unique_id}
                  style={{
                    backgroundColor: !isDarkMode ? '#FFFFFF' : 'white',
                    color: '#000000'
                  }}
                >
                  <td style={{ color: '#000000' }}>
                    {doc.unique_id.length > 10
                      ? `${doc.unique_id.slice(0, 10)}...`
                      : doc.unique_id}
                  </td>
                  <td style={{ color: '#000000' }}>{doc.title}</td>
                  <td style={{ color: '#000000' }}>
                    {new Date(doc.uploadedAt).toLocaleString()}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      onClick={() =>
                        window.open(`documents/${doc.unique_id}`, '_blank')
                      }
                      style={{
                        border: 'none',
                        backgroundColor: 'transparent',
                        cursor: 'pointer'
                      }}
                    >
                      <p
                        style={{
                          background: `${theme.colors.primary[0]}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'end',
                          gap: '0.5rem',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          color: '#fff',
                          border: 'none'
                        }}
                      >
                        View
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          height={20}
                          width={20}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                          />
                        </svg>
                      </p>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {loading && <Loader />}
      </section>
    </div>
  );
};
export default Documents;
